// One chart tile: dispatches a ready `ChartResult` to the right
// @bi/shared primitive by `viz_type` (design doc §3), and renders
// the loading/empty/error/stale states a single chart can be in (§7). Every
// adapter function this reads from lives in lib/chart-result-adapter.ts —
// this component's own job is purely the viz_type SWITCH and the render.
import {
  type FocusEvent as ReactFocusEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  BarChart,
  Button,
  DonutChart,
  FunnelChart,
  Gauge,
  LineAreaChart,
  SortableTh,
  StatTile,
  Tooltip,
  bucketIndexFromFraction,
  formatCompactCount,
  fractionAcross,
  useCategoricalColors,
  useSequentialColors,
  useTableSort,
} from "@bi/shared";
import type { FunnelHoverTarget, FunnelStageDatum, FunnelTransitionDatum } from "@bi/shared";
import type {
  BigNumberDisplay,
  ChartResult,
  ChartSpec,
  DatasetSpec,
  FunnelDisplay,
  GaugeDisplay,
  PieDisplay,
  TableDisplay,
  TimeSeriesDisplay,
} from "@bi/shared/types";

import {
  UnsupportedChartShape,
  bucketsFromResult,
  donutSlicesFromResult,
  funnelInputFromResult,
  gaugeInputFromResult,
  gaugeStatusColor,
  metricRefs,
  seriesFromResult,
  statValueFromResult,
  tableRowsFromResult,
} from "../lib/chart-result-adapter";
import type { ChartRenderState } from "../lib/bi-query";
import { type FunnelTip, funnelTip } from "../lib/funnel-tooltip";
import { formatCompactValue, formatNumber } from "../lib/number-format";
import { FunnelFlow } from "./funnel-flow";
import {
  type TimeGrain,
  axisEndLabelFor,
  bucketFormatterFor,
  grainChangeNote,
  resolveGrain,
  singleBucketNote,
} from "../lib/bucket-format";
import {
  collapseButtonLabel,
  expandButtonLabel,
  foldNote,
  isFoldable,
  previewRowCount,
  tableSummaryLabel,
} from "../lib/table-fold";
import type { ChartSizeClass } from "../lib/dashboard-layout";
import type { AuthoredConstraint } from "../lib/filter-state";
import { type PivotResult, pivotFromResult } from "../lib/pivot";
import {
  bucketModeForChart,
  isBrushable,
  normalizeIndexRange,
  timeBoundsFromBucketRange,
} from "../lib/time-brush";
import { ChartDownloadButton } from "./chart-download";
import { ChartModal } from "./chart-modal";
import { ClockIcon, InfoIcon } from "./icons";
import { describeChartError } from "../lib/chart-error";
import { errorMessage } from "./states";

/** True for the two viz_types a groupby-driven pivot can reach —
 *  `pivotFromResult` (lib/pivot.ts) only has a reshape for the timeseries
 *  family; `chart.query.groupby.length > 0` on any other viz_type still
 *  falls through to that viz_type's own adapter unchanged (a pie/table
 *  already reads a groupby column natively, see donutSlicesFromResult /
 *  tableRowsFromResult). */
function isPivotViz(vizType: ChartSpec["viz_type"]): boolean {
  return vizType === "echarts_timeseries_bar" || vizType === "echarts_timeseries_line";
}

/** How wide a single bar may be drawn, in `BarChart`'s 480-unit plot space.
 *  Without a cap a bar fills its whole step, so a one-bucket chart renders as
 *  a rectangle spanning the entire tile — the shape that stops reading as a
 *  bar at all. Roughly an eighth of the plot: wide enough to be a solid,
 *  comparable mark, narrow enough that it still reads as one bar sitting on
 *  its own bucket. */
const MAX_BAR_WIDTH = 56;

export interface ChartCardProps {
  chart: ChartSpec;
  state: ChartRenderState;
  /** How many of the grid's 12 columns this tile occupies, already normalized
   *  for its row (`lib/dashboard-layout.ts`'s `columnSpans`). Applied as an
   *  inline `gridColumn` rather than a class, because 12 possible spans is a
   *  number, not a set of variants. */
  columnSpan: number;
  sizeClass: ChartSizeClass;
  /** From `resolveFilterScope` — a filter that doesn't apply to THIS chart's
   *  dataset. Rendered as a small note, never hidden: a chart that's simply
   *  outside a filter's scope is a normal, expected state (design doc §4). */
  inactiveFilterCount: number;
  /** From `authoredConstraintsOnFilteredColumns` — this chart's OWN authored
   *  filters on columns the reader is filtering right now. A runtime filter
   *  narrows an authored one rather than replacing it, so these are why a
   *  chart can legitimately answer with nothing, and the reader is told which
   *  constraint is in play instead of being left with a blank panel. */
  authoredConstraints: readonly AuthoredConstraint[];
  /** From `chartIgnoresDateRange`, and only while a date range is actually
   *  picked. This chart has no temporal column, so the server scopes the
   *  range away rather than erroring the tile — which means the number shown
   *  covers ALL time while the rest of the dashboard is narrowed. Saying so
   *  is the difference between a time-independent number and a wrong one. */
  ignoresDateRange: boolean;
  /** What to say instead of "No data for this filter" when the result is empty.
   *  Optional, and the default is deliberately the old generic sentence: a
   *  caller that knows WHY a tile is empty (the studio does — see
   *  `lib/tile-empty.ts`) can say so, and one that does not must not guess. It
   *  never replaces the authored-constraint sentence, which is more specific
   *  still. */
  emptyExplanation?: string;
  /** From `chartTruncatesTimeOfDay`, and only while the reader's selection
   *  carries a time of day. This chart's temporal column is a plain DATE, so
   *  the server TRUNCATES the selection to day precision rather than refusing
   *  it. Never true together with `ignoresDateRange`. Optional because no
   *  caller passes it yet. */
  truncatesTimeOfDay?: boolean;
  /** This chart's own dataset (`datasets[chart.dataset_uuid]`) — needed here
   *  (not just by the page) to decide drag-to-zoom eligibility per chart via
   *  `isBrushable` (lib/time-brush.ts). `undefined` while the dashboard is
   *  still loading; brushing simply stays off until it resolves. */
  dataset: DatasetSpec | undefined;
  /** Applies a drag-selected bucket range as the DASHBOARD's date range.
   *  Omit to disable drag-to-zoom entirely for this card (e.g. a caller that
   *  hasn't wired the dashboard-wide callback yet). */
  onZoomToRange?: (start: string, end: string) => void;
  onRetry: () => void;
}

// Exported so ChartModal (the fullscreen view, chart-modal.tsx) can stamp
// the exact same "as of" wording rather than a second copy of this format.
export function computedAtLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `as of ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

export function ChartCard({
  chart,
  state,
  columnSpan,
  sizeClass,
  inactiveFilterCount,
  authoredConstraints,
  ignoresDateRange,
  emptyExplanation,
  truncatesTimeOfDay = false,
  dataset,
  onZoomToRange,
  onRetry,
}: ChartCardProps) {
  const constraintList = authoredConstraints.map((c) => c.label).join(", ");
  const isPivot = isPivotViz(chart.viz_type) && (chart.query.groupby?.length ?? 0) > 0;
  // A pivot's series come from the DATA, so colors cannot be keyed until a
  // result exists (pivot.ts explains the series order). Computed once here and
  // passed down so the color assignment and the rendered series use one fold.
  const pivot = useMemo<PivotResult | null>(() => {
    if (!isPivot || state.status !== "ready") return null;
    try {
      return pivotFromResult(chart, state.result);
    } catch {
      // An unhandled pivot shape (2+ groupby columns, etc.) — ChartCardReady
      // renders the UnsupportedChartShape message; colors fall back to the
      // metric-keyed default below rather than crashing this computation.
      return null;
    }
  }, [isPivot, chart, state]);
  // Every metric key this chart could ever declare — a stable input to
  // useCategoricalColors regardless of viz_type, so a chart that switches
  // from 1 to 2 metrics across a resync doesn't reshuffle color. Used
  // whenever this isn't a (successfully reshaped) pivot.
  const metricKeys = useMemo(
    () => metricRefs(chart.query).map((_m, i) => `metric-${i}`),
    [chart.query],
  );
  const seriesKeys = useMemo(
    () => (pivot ? pivot.series.map((s) => s.key) : metricKeys),
    [pivot, metricKeys],
  );
  // BOTH hooks run every render - rules of hooks, and neither is expensive (each
  // memoizes a Map keyed on the joined series list). Which one's output is USED
  // is `pivot.seriesScale`'s call: a date-cohort pivot arrives uncapped and in
  // date order, and coloring those 21 series from the 8-slot categorical palette
  // would drop every one past the 8th onto the same neutral --rc-chart-other
  // token - 14 indistinguishable lines where the ordering was the whole point.
  const categoricalColors = useCategoricalColors(seriesKeys);
  const sequentialColors = useSequentialColors(seriesKeys);
  const colors = pivot?.seriesScale === "sequential" ? sequentialColors : categoricalColors;

  // The bucket width this result was ACTUALLY compiled at, which is the
  // author's `time_grain_sqla` only until a reader picks a window it cannot
  // express (`recruiter/bi/compiler.py`'s `_effective_time_grain`). Both the
  // labels and the note below depend on knowing the real one.
  const readyBuckets = state.status === "ready" ? bucketsFromResult(chart, state.result) : [];
  const appliedGrain =
    state.status === "ready" ? resolveGrain(state.result.applied_time_grain, readyBuckets) : null;
  const grainNote = grainChangeNote(chart.query.time?.time_grain_sqla, appliedGrain);
  // A one-point "line" reads as a broken tile. Only for the chart forms that
  // actually draw a bucket sequence — a big_number or gauge legitimately has
  // one value and needs no explanation for it.
  const lonelyBucketNote = isPivotViz(chart.viz_type)
    ? singleBucketNote(readyBuckets.length, appliedGrain)
    : null;

  // A chart that already has data never regresses to a bare skeleton on a
  // filter-triggered refetch (design doc §5's "dimmed-stale, not blanked"
  // rule) — `state.refreshing` is bi-query.ts's signal that a new request is
  // in flight while `result` is still the last-known-good answer. Dimming
  // (not disguising) is what makes the difference visible: a reader must
  // never mistake a stale number for a current one.
  const refreshing = state.status === "ready" && state.refreshing === true;

  return (
    <figure
      className={`bi-card bi-card--${sizeClass}${refreshing ? " bi-card--refreshing" : ""}`}
      style={{ gridColumn: `span ${columnSpan}` }}
      aria-busy={state.status === "loading" || refreshing}
      // The chart's uuid, so an authored dashboard `css` can target one card.
      // An attribute, not an id: the same chart may appear on two dashboards.
      data-chart={chart.uuid}
      data-viz={chart.viz_type}
    >
      <figcaption className="bi-card__head">
        <span className="bi-card__title">{chart.slice_name}</span>
        {refreshing ? (
          <span className="bi-card__refresh-badge" title="Refreshing…" aria-label="Refreshing">
            <ClockIcon size={14} />
          </span>
        ) : null}
        {/* A real `Tooltip`, not `title`: `title` on a span wrapping an SVG
         *  shows nothing in practice and never shows on keyboard focus. The
         *  description stays the accessible name. */}
        {chart.description ? (
          <Tooltip label={chart.description}>
            <span className="bi-card__info" tabIndex={0} aria-label={chart.description}>
              <InfoIcon size={16} />
            </span>
          </Tooltip>
        ) : null}
        {/* Only a READY, non-empty tile has a result worth a fullscreen view or
         *  a download — loading has nothing to show yet, and error/empty read
         *  fine at card size with nothing behind them to save, so neither
         *  control renders for those states rather than opening onto a blank
         *  modal or writing a header-only file. */}
        {state.status === "ready" && state.result.row_count > 0 ? (
          <span className="bi-card__actions">
            <ChartDownloadButton chart={chart} result={state.result} className="bi-card__action" />
            <ChartModal
              chart={chart}
              result={state.result}
              colors={colors}
              pivot={pivot}
              inactiveFilterCount={inactiveFilterCount}
              constraintList={constraintList}
              ignoresDateRange={ignoresDateRange}
            />
          </span>
        ) : null}
      </figcaption>
      {inactiveFilterCount > 0 ? (
        <p className="bi-card__note">
          {inactiveFilterCount} filter{inactiveFilterCount === 1 ? "" : "s"} do
          {inactiveFilterCount === 1 ? "es" : ""} not apply to this chart
        </p>
      ) : null}
      {constraintList ? (
        <p className="bi-card__note">This chart is always limited to {constraintList}</p>
      ) : null}
      {ignoresDateRange ? (
        <p className="bi-card__note">
          This chart has no date column, so it covers all time regardless of the date range
        </p>
      ) : null}
      {truncatesTimeOfDay ? (
        <p className="bi-card__note">
          This chart's date column has no time of day, so the time you picked was rounded to a full
          day
        </p>
      ) : null}
      {/* The compiled bucket width, when it differs from the one this tile is
       *  TITLED after ("… per Day"). The caveat half of the caveat-vs-withhold
       *  rule: the bars are correct, the title's scope claim is what went
       *  stale, and one sentence repairs it. */}
      {grainNote ? <p className="bi-card__note">{grainNote}</p> : null}
      {/* Why this time series is a single dot. Not a finer grain's job: the
       *  warehouse tables these charts read are already aggregated per day,
       *  so there is no sub-day detail anywhere to zoom into. */}
      {lonelyBucketNote ? <p className="bi-card__note">{lonelyBucketNote}</p> : null}
      {/* Names the ramp, because a 21-swatch legend does not announce on its own
       *  that it is ORDERED - a reader who takes those for arbitrary category
       *  colors gets no more from this chart than a tangle. Only shown when
       *  nothing was folded, which for a sequential scale is always. */}
      {pivot?.seriesScale === "sequential" && !pivot.possiblyTruncated ? (
        <p className="bi-card__note">
          One line per date, all {pivot.totalSeriesCount} of them, colored as a sequence from{" "}
          {pivot.series[0]?.label} (lightest) to {pivot.series[pivot.series.length - 1]?.label}{" "}
          (darkest)
        </p>
      ) : null}
      {pivot && !pivot.possiblyTruncated && pivot.keptSeriesCount < pivot.totalSeriesCount ? (
        <p className="bi-card__note">
          Showing the top {pivot.keptSeriesCount} of {pivot.totalSeriesCount} series by total value,
          with the rest folded into Other
        </p>
      ) : null}
      {pivot?.possiblyTruncated ? (
        <p className="bi-card__note">
          This chart hit the server's row limit, so the underlying data is incomplete and
          nondeterministic. It is withheld below, since a partial sample could show a real cost or
          count as a false zero
        </p>
      ) : null}
      <div className="bi-card__body">
        {state.status === "loading" ? <ChartCardSkeleton sizeClass={sizeClass} /> : null}
        {state.status === "error" ? (
          <ChartCardError
            description={describeChartError(state.message, state.isTimeout)}
            onRetry={onRetry}
          />
        ) : null}
        {state.status === "ready" ? (
          state.result.row_count === 0 ? (
            <p className="bi-card__empty">
              {constraintList
                ? `No data: your filter is combined with this chart's own ${constraintList}, and nothing matches both.`
                : (emptyExplanation ?? "No data for this filter.")}
            </p>
          ) : (
            <ChartBrushZone
              chart={chart}
              dataset={dataset}
              result={state.result}
              grain={appliedGrain}
              onZoomToRange={onZoomToRange}
            >
              <ChartCardReady
                chart={chart}
                result={state.result}
                colors={colors}
                pivot={pivot}
                grain={appliedGrain}
              />
            </ChartBrushZone>
          )
        ) : null}
      </div>
      {state.status === "ready" ? (
        <p className="bi-card__stamp">{computedAtLabel(state.result.computed_at)}</p>
      ) : null}
    </figure>
  );
}

function ChartCardSkeleton({ sizeClass }: { sizeClass: ChartSizeClass }) {
  const height = sizeClass === "sm" ? 90 : sizeClass === "md" ? 140 : 220;
  return <div className="bi-card__skeleton" style={{ height }} aria-hidden="true" />;
}

/** The error tile. `describeChartError` (lib/chart-error.ts, pure + tested)
 *  decides the title and whether Retry is offered: a chart whose warehouse
 *  object is BEHIND THIS ENVIRONMENT'S PIN is the designed lag between the
 *  catalog train and the warehouse train, not a failure, so it reads as a
 *  status note (`role="status"`) with no Retry, since every attempt gets the
 *  same answer until the pin moves. A chart missing a COLUMN keeps the alert
 *  role, on purpose: it is equally unretryable, but the service cannot tell a
 *  not-yet-pinned column from a misspelled one, and dressing a possible
 *  authoring bug as a status note would hide it. */
function ChartCardError({
  description,
  onRetry,
}: {
  description: ReturnType<typeof describeChartError>;
  onRetry: () => void;
}) {
  const behindPin = description.kind === "behind_pin";
  return (
    <div
      className={behindPin ? "bi-card__error bi-card__error--behind-pin" : "bi-card__error"}
      role={behindPin ? "status" : "alert"}
    >
      <p className="bi-card__error-title">{description.title}</p>
      <p className="bi-card__error-message">{description.message}</p>
      {description.retryable ? (
        <button type="button" className="bi-card__retry" onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </div>
  );
}

/** Rendered instead of `BarChart`/`LineAreaChart` when `pivot.possiblyTruncated`.
 *  A withhold, not a caveat: `pivotFromResult` zero-fills missing
 *  `(series, bucket)` pairs, which fabricates false zeros for a TRUNCATED
 *  result. Rule: caveat when the number is correct but scoped differently
 *  (`ignoresDateRange`, `authoredConstraints`), withhold when the number would
 *  be FALSE. No Retry: the cap is the server's default row limit, so a retry
 *  hits the same limit. */
function PivotTruncatedMessage() {
  return (
    <p className="bi-card__error-message">
      Chart withheld. The data behind it came back incomplete and nondeterministic (the server's row
      limit), and there is no reliable way to draw it without risking a false zero for real
      activity. Retrying will not help, the limit is a backend constraint, not a transient failure.
    </p>
  );
}

export interface ChartCardReadyProps {
  chart: ChartSpec;
  result: ChartResult;
  colors: Map<string, string>;
  /** The already-reshaped groupby-driven multi-series result, when `ChartCard`
   *  determined this chart is one (see `isPivotViz`) and its reshape
   *  succeeded — `null` for every ordinary chart, and also `null` for an
   *  UNHANDLED pivot shape (2+ groupby columns, etc.), which falls through to
   *  `seriesFromResult` below and surfaces as that function's own
   *  `UnsupportedChartShape`, caught the same way any other adapter failure is.
   *
   *  REQUIRED on purpose, even though `ChartModal` would compile without it if
   *  it were optional: this prop carries the WITHHOLD decision for a truncated
   *  pivot, and an optional one would let the fullscreen view draw the very
   *  zero-filled bars the card refuses to draw. A reader who opens a chart
   *  fullscreen to study it closely is the last person who should be shown the
   *  fabricated version, so a new caller must not be able to forget it. */
  pivot: PivotResult | null;
  /** True only when rendering into the fullscreen modal. Today it makes a
   *  `table` render fully open instead of folded, because the reader opened it
   *  fullscreen to see all of it. A presentation-context flag, not a
   *  table-specific one, so another viz_type can hang fullscreen behavior on it. */
  expandedPresentation?: boolean;
  /** The grain this result's bucket labels are at, for formatting them. See
   *  `lib/bucket-format.ts` — a day bucket rendered by the primitives' own
   *  default formatter reads as a LOCAL clock time, which is the same string
   *  for every day on the chart. `null` for a categorical x-axis, where the
   *  labels are already what the reader should see. */
  grain: TimeGrain | null;
}

/** Dispatches a READY, non-empty result to the right primitive. Split out
 *  from `ChartCard` so the viz_type switch (and its exhaustiveness check)
 *  isn't tangled with the loading/error/empty branches above it. Exported so
 *  ChartModal (chart-modal.tsx) can reuse this exact dispatch at fullscreen
 *  size instead of a second switch on `viz_type` that would drift from this
 *  one — the whole point of keeping one module owning the mapping. */
export function ChartCardReady({
  chart,
  result,
  colors,
  pivot,
  grain,
  expandedPresentation = false,
}: ChartCardReadyProps) {
  try {
    switch (chart.viz_type) {
      case "echarts_timeseries_bar": {
        if (pivot?.possiblyTruncated) return <PivotTruncatedMessage />;
        const display = chart.display as TimeSeriesDisplay;
        const { buckets, series } = pivot ?? {
          buckets: bucketsFromResult(chart, result),
          series: seriesFromResult(chart, result),
        };
        const layout =
          display.stack === "Stack" ? "stacked" : series.length > 1 ? "grouped" : "single";
        return (
          <BarChart
            title={chart.slice_name}
            buckets={buckets}
            series={series}
            colors={colors}
            layout={layout}
            // Only meaningful on a single series; the primitive ignores it
            // otherwise, and a grouped diverging chart has no one baseline.
            diverging={display.diverging}
            xAxisTitle={display.x_axis_title ?? undefined}
            yAxisTitle={display.y_axis_title ?? undefined}
            windowLabel=""
            hoverTooltip
            maxBarWidth={MAX_BAR_WIDTH}
            formatBucket={bucketFormatterFor(grain)}
            axisEndLabel={axisEndLabelFor(buckets, grain)}
          />
        );
      }
      case "echarts_timeseries_line": {
        if (pivot?.possiblyTruncated) return <PivotTruncatedMessage />;
        const lineDisplay = chart.display as TimeSeriesDisplay;
        const { buckets, series } = pivot ?? {
          buckets: bucketsFromResult(chart, result),
          series: seriesFromResult(chart, result),
        };
        // A line needs two points to BE a line. With one bucket it draws a
        // flat stub with no slope, which reads as a broken tile rather than
        // as the number it is — and the underlying warehouse tables are
        // aggregated per day, so a narrow window genuinely HAS one row and no
        // finer grain can produce a second (see `singleBucketNote`). Bars
        // state a magnitude honestly at any count, so a single-bucket
        // timeseries renders as one, with the same buckets, series, colors,
        // formatter and tooltip as every other tile.
        if (buckets.length === 1) {
          return (
            <BarChart
              title={chart.slice_name}
              buckets={buckets}
              series={series}
              colors={colors}
              layout={series.length > 1 ? "grouped" : "single"}
              windowLabel=""
              xAxisTitle={lineDisplay.x_axis_title ?? undefined}
              yAxisTitle={lineDisplay.y_axis_title ?? undefined}
              hoverTooltip
              maxBarWidth={MAX_BAR_WIDTH}
              formatBucket={bucketFormatterFor(grain)}
              axisEndLabel={axisEndLabelFor(buckets, grain)}
            />
          );
        }
        return (
          <LineAreaChart
            title={chart.slice_name}
            buckets={buckets}
            series={series}
            colors={colors}
            variant={series.length === 1 ? "area" : "line"}
            xAxisTitle={lineDisplay.x_axis_title ?? undefined}
            yAxisTitle={lineDisplay.y_axis_title ?? undefined}
            hoverTooltip
            formatBucket={bucketFormatterFor(grain)}
            axisEndLabel={axisEndLabelFor(buckets, grain)}
          />
        );
      }
      case "big_number_total":
      case "big_number": {
        const display = chart.display as BigNumberDisplay;
        const { value, spark } = statValueFromResult(chart, result);
        // The author's own `y_axis_format` decides the UNIT, and getting the
        // unit wrong is not a formatting nit: `formatCompactCount` alone
        // printed a rate of 0.42 as "0" and a dollar total as a bare count, on
        // a tile whose only other words are its title. `formatNumber` returns
        // null for a format outside the supported subset (never a guess), and
        // then the compact count is still the right default.
        return (
          <StatTile
            label={chart.slice_name}
            value={formatNumber(value, display.y_axis_format) ?? formatCompactCount(value)}
            sub={display.subheader ?? undefined}
            accent={spark ? "var(--rc-color-accent)" : undefined}
            spark={spark}
          />
        );
      }
      case "gauge_chart": {
        const display = chart.display as GaugeDisplay;
        const { ratio, value } = gaugeInputFromResult(chart, result);
        return (
          <Gauge
            label={chart.slice_name}
            ratio={ratio}
            display={formatCompactCount(value)}
            color={gaugeStatusColor(ratio, display)}
            hoverTooltip
          />
        );
      }
      case "pie": {
        const display = chart.display as PieDisplay;
        return (
          <DonutChart
            title={chart.slice_name}
            slices={donutSlicesFromResult(chart, result)}
            totalLabel={display.donut ? "total" : undefined}
            hoverTooltip
          />
        );
      }
      case "table": {
        const display = chart.display as TableDisplay;
        return (
          <ChartCardTable
            result={result}
            serverPagination={display.server_pagination ?? false}
            forceExpanded={expandedPresentation}
          />
        );
      }
      case "funnel": {
        // The sankey lifecycle funnel, the same @bi/shared component the
        // admin console renders — one implementation, so the two cannot drift
        // and disagree about the same question. Here it is read-only: no
        // `onActivate`, because this app has no Explorer to open, and the
        // component then draws no click affordance and no button roles at all.
        //
        // Its own component (rather than a `<FunnelChart>` inline here) because
        // the hover card is React state, and a `useState` inside one arm of a
        // switch is a conditional hook.
        const display = chart.display as FunnelDisplay;
        const { stages, transitions } = funnelInputFromResult(display, result);
        if (display.outcome_stage_keys?.length) {
          return <FunnelFlow display={display} stages={stages} transitions={transitions} />;
        }
        return <ChartCardFunnel display={display} stages={stages} transitions={transitions} />;
      }
      default: {
        // VizType is a closed literal union this app owns (mirrors the
        // backend's own closed set) — a new value here is a build-time
        // failure, not a silent blank card.
        const _exhaustive: never = chart.viz_type;
        return <p className="bi-card__error-message">Unknown chart type {String(_exhaustive)}.</p>;
      }
    }
  } catch (err) {
    if (err instanceof UnsupportedChartShape) {
      return <p className="bi-card__error-message">{err.message}</p>;
    }
    return <p className="bi-card__error-message">{errorMessage(err)}</p>;
  }
}

/**
 * The sankey funnel plus its hover card. `FunnelChart` reports WHICH mark the
 * pointer is on (a `FunnelHoverTarget`); this app decides what the card says
 * from the funnel's own numbers (`lib/funnel-tooltip.ts`, which owns every
 * denominator). The card follows the pointer, clamped inside the tile.
 */
function ChartCardFunnel({
  display,
  stages,
  transitions,
}: {
  display: FunnelDisplay;
  stages: readonly FunnelStageDatum[];
  transitions: readonly FunnelTransitionDatum[];
}) {
  const [tip, setTip] = useState<(FunnelTip & { x: number; y: number }) | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);

  // Two renderings of one authored format, and the split is deliberate (see
  // `number-format.ts`): the bar labels sit INSIDE the drawing and get the
  // compact form, the card has room and gets the exact one.
  const compact = (value: number) => formatCompactValue(value, display.value_format);
  const exact = (value: number) =>
    formatNumber(value, display.value_format) ?? value.toLocaleString("en-US");

  const showTip = (target: FunnelHoverTarget, event: ReactPointerEvent | ReactFocusEvent) => {
    const host = hostRef.current;
    if (!host) return;
    const next = funnelTip(target, {
      stages,
      transitions,
      unitLabel: display.unit_label,
      // NULL, not the label, when the funnel draws no sub-series: the copy
      // must not name a hatch that is not on screen. `secondary_reached_column`
      // is what decides whether one exists at all (`FunnelDisplay`).
      secondaryLabel: display.secondary_reached_column ? display.secondary_series_label : null,
      formatValue: exact,
    });
    if (!next) return;
    const bounds = host.getBoundingClientRect();
    const box = (event.currentTarget as Element).getBoundingClientRect();
    // A focus event carries no pointer position, so a keyboard user gets the
    // card anchored over the mark itself rather than nowhere.
    const pointer = "clientX" in event ? event : null;
    const rawX = pointer ? pointer.clientX - bounds.left : box.left - bounds.left + box.width / 2;
    const rawY = pointer ? pointer.clientY - bounds.top : box.top - bounds.top;
    setTip({
      ...next,
      x: Math.min(
        Math.max(rawX, TIP_HALF_WIDTH),
        Math.max(bounds.width - TIP_HALF_WIDTH, TIP_HALF_WIDTH),
      ),
      y: Math.max(rawY - 12, 8),
    });
  };

  return (
    <div className="bi-funnel" ref={hostRef}>
      <FunnelChart
        stages={stages}
        transitions={transitions}
        // No colorForStage: `colors` is keyed by metric/series NAME, and a
        // funnel's marks are stages, so a lookup here could only ever miss and
        // fall through. The component's own default is the same
        // --rc-chart-1..8 sequence anyway, so the honest version is to let it
        // do that rather than route through a Map that never hits.
        //
        // The nouns come from the SPEC, not from here: this app renders any
        // funnel the warehouse defines, and only its author knows whether a bar
        // is 80 projects, 80 sellers or 80 briefs. The numbers ARE the message
        // for a screen-reader user, so an unnamed one is not one.
        unitLabel={display.unit_label}
        secondarySeriesLabel={display.secondary_series_label}
        formatValue={compact}
        onHover={showTip}
        onLeave={() => setTip(null)}
      />
      {tip ? (
        <div className="bi-funnel__tip" style={{ left: tip.x, top: tip.y }} role="presentation">
          <div className="bi-funnel__tip-title">{tip.title}</div>
          {tip.rows.map((row) => (
            <div key={`${row.value}-${row.label}`} className="bi-funnel__tip-row">
              <strong>{row.value}</strong> <span>{row.label}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Half the hover card's own width, in px — the clamp that keeps it inside a
 *  narrow tile. Mirrors `.bi-funnel__tip`'s `width` in globals.css. */
const TIP_HALF_WIDTH = 120;

/** Movement below this many px, at pointerup, means "click", not "drag" —
 *  the pointer-to-bucket math never even runs. */
const DRAG_THRESHOLD_PX = 4;

interface DragState {
  /** The `.rc-chart__svg` element's own box, captured ONCE at pointerdown —
   *  BarChart/LineAreaChart lay out a fixed, no-padding plot area (design
   *  doc note this feature was scoped against), so a single measurement
   *  covers the whole gesture; nothing here needs to re-measure on move. */
  svgRect: DOMRect;
  /** The brush wrapper's OWN box at pointerdown, in the same coordinate
   *  space as `svgRect` — used only to translate the svg-relative selection
   *  highlight into wrapper-relative CSS px (the wrapper has padding around
   *  the svg that a plain percentage-of-wrapper-width would misalign with). */
  wrapperRect: DOMRect;
  startClientX: number;
  startFraction: number;
  currentFraction: number;
  pointerId: number;
}

/**
 * Drag-to-zoom over a `BarChart`/`LineAreaChart` plot area: a drag selects a
 * bucket range and release applies it as the dashboard's date range via
 * `onZoomToRange`. A chart `isBrushable` refuses renders `{children}` inert.
 *
 * Pointer-to-bucket math is `@bi/shared`'s chart-hover.ts (the same function
 * the hover tooltip uses). Bucket range -> date bounds is `lib/time-brush.ts`.
 * This component owns only the gesture: click vs drag, pointer capture, the
 * selection rectangle.
 *
 * No keyboard equivalent: the filter bar's presets stay the keyboard path.
 */
function ChartBrushZone({
  chart,
  dataset,
  result,
  grain,
  onZoomToRange,
  children,
}: {
  chart: ChartSpec;
  dataset: DatasetSpec | undefined;
  result: ChartResult;
  /** The bucket width the result was compiled at — what decides whether a
   *  drag can express a moment or only a calendar day. */
  grain: TimeGrain | null;
  onZoomToRange?: (start: string, end: string) => void;
  children: ReactNode;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  // Only the fractions actually drive a render (the live selection
  // rectangle); everything else in DragState is read-only bookkeeping
  // between pointerdown and the next event, so it stays in a ref.
  const [selection, setSelection] = useState<{ start: number; current: number } | null>(null);

  // Computed unconditionally (cheap and pure) so `isBrushable` can verify a
  // REAL sample label converts, not just the chart's static declarations —
  // see time-brush.ts's own comment on why that check exists.
  const buckets = bucketsFromResult(chart, result);
  const brushable = Boolean(onZoomToRange) && isBrushable(chart, dataset, buckets);
  if (!brushable) {
    return <div className="bi-brush-zone">{children}</div>;
  }

  const mode = bucketModeForChart(chart);

  const endDrag = () => {
    dragRef.current = null;
    setSelection(null);
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const svg = wrapperRef.current?.querySelector<SVGSVGElement>("svg.rc-chart__svg");
    const wrapper = wrapperRef.current;
    if (!svg || !wrapper) return;
    const svgRect = svg.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const startFraction = fractionAcross(event.clientX, svgRect);
    dragRef.current = {
      svgRect,
      wrapperRect,
      startClientX: event.clientX,
      startFraction,
      currentFraction: startFraction,
      pointerId: event.pointerId,
    };
    setSelection({ start: startFraction, current: startFraction });
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    drag.currentFraction = fractionAcross(event.clientX, drag.svgRect);
    setSelection({ start: drag.startFraction, current: drag.currentFraction });
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      endDrag();
      return;
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const movedPx = Math.abs(event.clientX - drag.startClientX);
    endDrag();
    if (movedPx < DRAG_THRESHOLD_PX) return; // a click, not a drag.
    const startIdx = bucketIndexFromFraction(drag.startFraction, buckets.length, mode);
    const endIdx = bucketIndexFromFraction(drag.currentFraction, buckets.length, mode);
    if (startIdx === null || endIdx === null) return;
    const { lo, hi } = normalizeIndexRange(startIdx, endIdx);
    const bounds = timeBoundsFromBucketRange(buckets, lo, hi, grain);
    // `bounds` is null for a non-parseable bucket label — refuse silently
    // (time-brush.ts's own contract) rather than apply a guessed range.
    if (bounds) onZoomToRange?.(bounds.start, bounds.end);
  };

  const onPointerCancel = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) endDrag();
  };

  return (
    <div
      ref={wrapperRef}
      className="bi-brush-zone bi-brush-zone--active"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      {children}
      {selection && dragRef.current ? (
        <BrushSelectionOverlay
          startFraction={Math.min(selection.start, selection.current)}
          endFraction={Math.max(selection.start, selection.current)}
          svgRect={dragRef.current.svgRect}
          wrapperRect={dragRef.current.wrapperRect}
        />
      ) : null}
    </div>
  );
}

/** The live drag-selection rectangle, positioned in CSS px relative to the
 *  brush wrapper (never a percentage of the wrapper's own width — the
 *  wrapper carries card padding the svg doesn't, so a percentage would
 *  visibly drift from the plot area it's supposed to highlight). */
function BrushSelectionOverlay({
  startFraction,
  endFraction,
  svgRect,
  wrapperRect,
}: {
  startFraction: number;
  endFraction: number;
  svgRect: DOMRect;
  wrapperRect: DOMRect;
}) {
  const left = svgRect.left - wrapperRect.left + startFraction * svgRect.width;
  const width = Math.max((endFraction - startFraction) * svgRect.width, 1);
  const top = svgRect.top - wrapperRect.top;
  return (
    <div
      className="bi-brush-zone__selection"
      aria-hidden="true"
      style={{ left, width, top, height: svgRect.height }}
    />
  );
}

function ChartCardTable({
  result,
  serverPagination,
  forceExpanded = false,
}: {
  result: ChartResult;
  serverPagination: boolean;
  /** Renders fully open regardless of row count. Set from
   *  `expandedPresentation` in the `case "table":` arm (the fullscreen modal). */
  forceExpanded?: boolean;
}) {
  const { columns, rows } = tableRowsFromResult(result);
  const accessors = useMemo(
    () =>
      Object.fromEntries(
        columns.map((col, i) => [
          col,
          (row: unknown[]) => row[i] as string | number | boolean | null,
        ]),
      ),
    [columns],
  ) as Record<string, (row: unknown[]) => string | number | boolean | null>;
  const { rows: sorted, toggle, ariaSort } = useTableSort(rows, accessors);

  // A table this short reads fine flat, so it offers no fold at all — the
  // control itself, not just the default state, only exists once `isFoldable`
  // says there's something worth collapsing (table-fold.ts). Recomputed every
  // render (not just at mount) so a table that RE-settles with fewer rows
  // (a retry, a narrower filter) never gets stuck showing a stale fold UI for
  // data that no longer warrants one.
  const foldable = !forceExpanded && isFoldable(rows.length);
  // Only the reader's own toggle is state. A foldable table starts collapsed.
  // `foldable` changing mid-session can turn the fold on or off but never
  // overrides the reader's choice.
  const [userExpanded, setUserExpanded] = useState(false);
  const collapsed = foldable && !userExpanded;
  const tableBodyId = useId();

  const visibleRows = collapsed ? sorted.slice(0, previewRowCount(rows.length)) : sorted;
  const note = foldNote({ collapsed, rowCount: rows.length, serverPagination });

  return (
    <div className="bi-card__table-wrap">
      {foldable ? (
        <div className="bi-table-fold">
          <span className="bi-table-fold__summary">
            {tableSummaryLabel(rows.length, columns.length)}
          </span>
          <Button
            variant="ghost"
            tone="neutral"
            size="sm"
            aria-expanded={!collapsed}
            aria-controls={tableBodyId}
            onClick={() => setUserExpanded((expanded) => !expanded)}
          >
            {collapsed ? expandButtonLabel(rows.length) : collapseButtonLabel(rows.length)}
          </Button>
        </div>
      ) : null}
      {note ? <p className="bi-card__note">{note}</p> : null}
      <table className="rc-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <SortableTh key={col} sortKey={col} ariaSort={ariaSort} onToggle={toggle}>
                {col}
              </SortableTh>
            ))}
          </tr>
        </thead>
        <tbody id={tableBodyId}>
          {visibleRows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell === null || cell === undefined ? "" : String(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
